import React, { useState, useEffect, ChangeEvent, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useMst } from '../../model/Root';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import Table from '../../components/Table/Table';
import AddButton from '../../components/Button/AddButton';
import { useTranslation } from 'react-i18next';
import { USER_TABLE, UserRole } from '../../utils/constants';
import { fetchUsers } from 'services/api';
import {
  Row,
  LinkRow,
  Subscription,
  User,
  PortalSubscriptionPlatform,
  PORTAL_SUBSCRIPTION_PLATFORMS,
  PORTAL_STRIPE_KINDS,
  PortalStripeKind,
  PortalUserFilters,
  PortalUsersResponse,
} from '../../types';
import { getUserRoleByLocation, getUserTitleByLocation, getUserUrlByLocation } from 'utils/userUtils';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  OutlinedInput,
  Pagination,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import ClearIcon from '@mui/icons-material/Clear';

const PORTAL_TABLE_HEADERS = [
  'user_name',
  'portal_users.active_subscription',
  'portal_users.platform',
  'portal_users.period_ends',
  'user_role',
];

const PORTAL_TABLE_TEMPLATE = '30% 23% 15% 15% 17%';
const PORTAL_PAGE_SIZE_OPTIONS = [50, 100, 200];
const PORTAL_DEFAULT_LIMIT = 100;

// Formats a Date object as 'YYYY-MM-DD'. Returns '' for invalid or out-of-range input.
const formatDateInput = (date: Date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) {
    return '';
  }
  const year = date.getUTCFullYear();
  if (year < 1970 || year > 9999) {
    return '';
  }
  return date.toISOString().slice(0, 10);
};

// Formats a date-like value for CSV. Returns '' for invalid input.
const formatDateForCsv = (value?: string | null) => {
  if (!value) return '';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '';
  const year = parsed.getUTCFullYear();
  if (year < 1970 || year > 9999) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

// Escapes a value for CSV. Returns empty quotes for unsupported inputs.
const escapeCsvValue = (value: string | number | boolean | null | undefined) => {
  if (value === null || value === undefined) return '""';
  if (typeof value === 'number') {
    if (!Number.isFinite(value) || Math.abs(value) > Number.MAX_SAFE_INTEGER) {
      return '""';
    }
  } else if (typeof value === 'object') {
    return '""';
  }
  const stringValue = String(value).replace(/"/g, '""');
  return `"${stringValue}"`;
};

const coerceNumber = (value: unknown): number | undefined => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string' && value.trim().length > 0) {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return undefined;
};

const coerceBoolean = (value: unknown): boolean | undefined => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') {
    if (value === 1) return true;
    if (value === 0) return false;
  }
  if (typeof value === 'string') {
    const normalized = value.trim().toLowerCase();
    if (normalized === 'true') return true;
    if (normalized === 'false') return false;
  }
  return undefined;
};

const findSubscriptionById = (subscriptions: User['subscriptions'] | undefined, subscriptionId?: number | null) => {
  if (!subscriptions || subscriptions.length === 0) return undefined;
  if (!subscriptionId) return undefined;
  return subscriptions.find((s) => s.id === subscriptionId);
};

const UsersScreen = observer(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { pathname } = window.location;
  const {
    userStore: {
      users,
      getUsersByRole,
      loadPortalUsersWithFilters,
      resetPortalFilters,
      loadPortalStats,
      portalFilters,
      portalStats,
      portalPagination,
      portalUsersLoading,
      portalStatsLoading,
      forcePortalSubscriptionExpiry,
    },
    errorStore: { setError },
  } = useMst();
  const [selectedRow, setSelectedRow] = useState<Row | LinkRow | null>(null);
  const [filter, setFilter] = useState('');
  const [portalSearchInput, setPortalSearchInput] = useState(portalFilters.search ?? '');
  const userRole = getUserRoleByLocation(pathname);
  const isPortalView = userRole === UserRole.portal;
  const [exportingCsv, setExportingCsv] = useState(false);
  const [forceExpiryDialog, setForceExpiryDialog] = useState<{
    open: boolean;
    user?: User;
    subscription?: Subscription | null;
  }>({ open: false });
  const [forceExpiryDate, setForceExpiryDate] = useState('');
  const [forceExpiryMarkInactive, setForceExpiryMarkInactive] = useState(true);
  const [forceExpiryError, setForceExpiryError] = useState<string | null>(null);
  const [forceExpirySubmitting, setForceExpirySubmitting] = useState(false);

  useEffect(() => {
    if (isPortalView) {
      loadPortalUsersWithFilters();
    } else {
      getUsersByRole(userRole);
    }
  }, [getUsersByRole, isPortalView, loadPortalUsersWithFilters, userRole]);

  useEffect(() => {
    if (!isPortalView) return;
    // Widgets are constant: fetch once with no query params.
    loadPortalStats();
  }, [isPortalView, loadPortalStats]);

  useEffect(() => {
    if (!isPortalView) {
      return;
    }
    // Sync initial search value when entering portal view
    setPortalSearchInput(portalFilters.search ?? '');
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isPortalView]);

  // Debounce portal search input -> backend search param (no local-only filtering)
  useEffect(() => {
    if (!isPortalView) return;
    const next = portalSearchInput;
    const current = portalFilters.search ?? '';
    if (next.trim() === current.trim()) return;
    const handle = window.setTimeout(() => {
      loadPortalUsersWithFilters({ search: next, offset: 0 });
    }, 400);
    return () => window.clearTimeout(handle);
  }, [isPortalView, loadPortalUsersWithFilters, portalFilters.search, portalSearchInput]);

  const filteredData = (() => {
    const list = users.slice(); // reactive copy from MST
    if (isPortalView) {
      return list;
    }
    if (!filter.trim()) {
      return list;
    }
    const normalized = filter.toLowerCase();
    return list.filter(
      (user) =>
        user.firstname?.toLowerCase().includes(normalized) ||
        user.lastname?.toLowerCase().includes(normalized) ||
        user.email?.toLowerCase().includes(normalized)
    );
  })();

  const portalLimit = portalFilters.limit ?? portalPagination.limit ?? PORTAL_DEFAULT_LIMIT;
  const portalOffset = portalFilters.offset ?? portalPagination.offset ?? 0;
  const safePortalLimit = Math.max(portalLimit, 1);
  const portalTotalRaw = portalPagination.total ?? 0;
  const portalHasMore = Boolean(portalPagination.hasMore);
  const hasExplicitTotal = portalTotalRaw > 0;
  const hasCurrentPageData = users.length > 0;
  // Fallback only when backend total is unavailable; do not inflate totals.
  const portalTotal = hasExplicitTotal ? portalTotalRaw : hasCurrentPageData ? portalOffset + users.length : 0;
  const portalTotalPages = Math.max(1, Math.ceil(portalTotal / safePortalLimit) || 1);
  const portalCurrentPage = portalTotal > 0 ? Math.floor(portalOffset / safePortalLimit) + 1 : 1;
  const portalShowingFrom = portalTotal === 0 ? 0 : portalOffset + 1;
  const portalShowingTo = portalTotal === 0 ? 0 : Math.min(portalTotal, portalOffset + users.length);
  const disablePortalPagination = portalTotal === 0;
  const canGoPrev = portalOffset > 0 && !portalUsersLoading;
  const canGoNext =
    (hasExplicitTotal ? portalOffset + users.length < portalTotalRaw : portalHasMore) && !portalUsersLoading;

  const handlePortalPageChange = (_: ChangeEvent<unknown>, newPage: number) => {
    if (!isPortalView) return;
    const nextPage = Math.min(Math.max(newPage, 1), portalTotalPages);
    const nextOffset = (nextPage - 1) * safePortalLimit;
    if (nextOffset === portalOffset) return;
    loadPortalUsersWithFilters({ offset: nextOffset });
  };

  const handlePortalPrev = () => {
    if (!canGoPrev) return;
    loadPortalUsersWithFilters({ offset: Math.max(portalOffset - safePortalLimit, 0) });
  };

  const handlePortalNext = () => {
    if (!canGoNext) return;
    loadPortalUsersWithFilters({ offset: portalOffset + safePortalLimit });
  };

  const handlePortalPageSizeChange = (event: SelectChangeEvent<string>) => {
    if (!isPortalView) return;
    const newLimit = Number(event.target.value);
    if (Number.isNaN(newLimit) || newLimit === portalLimit) return;
    loadPortalUsersWithFilters({ limit: newLimit, offset: 0 });
  };

  const handleDownloadPortalCsv = async () => {
    if (!isPortalView || exportingCsv) {
      return;
    }
    try {
      setExportingCsv(true);
      const pageSize = 200; // backend contract max limit
      const filters: PortalUserFilters = {
        platform: portalFilters.platform.slice() as PortalSubscriptionPlatform[],
        stripeKind: portalFilters.stripeKind ?? '',
        codeOnly: portalFilters.codeOnly,
        activeOnly: portalFilters.activeOnly,
        missingEndDate: portalFilters.missingEndDate,
        marketing: portalFilters.marketing,
        search: portalFilters.search?.trim() ?? '',
      };

      const allUsers: User[] = [];
      let offset = 0;
      let total = portalPagination.total ?? 0;
      let continueFetching = true;

      while (continueFetching) {
        const response = await fetchUsers(UserRole.portal, { ...filters, limit: pageSize, offset });
        const payload = response.data as PortalUsersResponse | User[];
        const pageData = Array.isArray(payload) ? payload : (payload?.data ?? []);
        const pagination = !Array.isArray(payload)
          ? ((payload as unknown as Record<string, unknown>)?.pagination as Record<string, unknown> | undefined)
          : undefined;
        const paginationTotal = pagination ? coerceNumber(pagination.total) : undefined;
        const paginationHasMore = pagination ? coerceBoolean(pagination.hasMore) : undefined;
        if (paginationTotal !== undefined) {
          total = Math.max(total, paginationTotal);
        } else if (total === 0) {
          total = Math.max(total, offset + pageData.length);
        }
        allUsers.push(...pageData);

        const hasMore =
          paginationHasMore !== undefined
            ? paginationHasMore
            : paginationTotal !== undefined
              ? offset + pageData.length < paginationTotal
              : pageData.length === pageSize;

        if (!hasMore) {
          continueFetching = false;
        } else {
          offset += pageSize;
        }
      }

      const header = [
        'id',
        'firstname',
        'lastname',
        'email',
        'marketing',
        'permission',
        'platform',
        'status',
        'periodEnds',
      ];
      const rows = allUsers.map((user) => {
        const activeSub = findSubscriptionById(user.subscriptions, user.activeSubscriptionId);
        const latestSub = findSubscriptionById(user.subscriptions, user.latestSubscriptionId);
        const primarySub = activeSub ?? latestSub;
        const platform = user.activeSubscriptionPlatform ?? user.latestSubscriptionPlatform ?? '';
        return [
          escapeCsvValue(user.id ?? ''),
          escapeCsvValue(user.firstname ?? ''),
          escapeCsvValue(user.lastname ?? ''),
          escapeCsvValue(user.email ?? ''),
          escapeCsvValue(user.marketing ?? false),
          escapeCsvValue(user.permission ?? false),
          escapeCsvValue(platform),
          escapeCsvValue(primarySub?.status ?? primarySub?.state ?? ''),
          escapeCsvValue(formatDateForCsv(primarySub?.periodEnds)),
        ].join(',');
      });
      const csvString = [header.join(','), ...rows].join('\n');
      const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'portal_users.csv';
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error(err);
      setError('general_error_message');
    } finally {
      setExportingCsv(false);
    }
  };

  const openForceExpiryDialogForSubscription = (user: User, subscription: Subscription) => {
    const existingDate =
      subscription.periodEnds && !Number.isNaN(new Date(subscription.periodEnds).getTime())
        ? formatDateInput(new Date(subscription.periodEnds))
        : '';
    setForceExpiryDialog({ open: true, user, subscription });
    setForceExpiryDate(existingDate);
    setForceExpiryMarkInactive(true);
    setForceExpiryError(null);
  };

  const closeForceExpiryDialog = () => {
    setForceExpiryDialog({ open: false });
    setForceExpirySubmitting(false);
    setForceExpiryError(null);
    setForceExpiryDate('');
    setForceExpiryMarkInactive(true);
  };

  const submitForceExpiry = async () => {
    if (!forceExpiryDialog.subscription) {
      return;
    }
    if (!forceExpiryDate) {
      setForceExpiryError(t('portal_users.fix_missing_end_date_period_required'));
      return;
    }
    const parsed = new Date(forceExpiryDate);
    if (Number.isNaN(parsed.getTime())) {
      setForceExpiryError(t('portal_users.fix_missing_end_date_period_required'));
      return;
    }
    const parsedValue = parsed.toISOString();
    setForceExpirySubmitting(true);
    try {
      await forcePortalSubscriptionExpiry({
        subscriptionId: forceExpiryDialog.subscription.id,
        userId: forceExpiryDialog.user?.id ?? undefined,
        periodEnds: parsedValue,
        valid: forceExpiryMarkInactive ? false : undefined,
      });
      closeForceExpiryDialog();
    } catch (err) {
      console.error(err);
      setError('portal_users.force_expiry_error');
    } finally {
      setForceExpirySubmitting(false);
    }
  };

  const handlePlatformChange = (event: SelectChangeEvent<string[]>) => {
    const value = event.target.value;
    const nextValue =
      typeof value === 'string'
        ? (value.split(',') as PortalSubscriptionPlatform[])
        : (value as PortalSubscriptionPlatform[]);
    loadPortalUsersWithFilters({ platform: nextValue });
  };

  const handleStripeKindChange = (event: SelectChangeEvent<string>) => {
    const value = String(event.target.value ?? '');
    const nextValue = (value === 'ajassa' || value === 'rv' ? value : '') as PortalStripeKind | '';
    loadPortalUsersWithFilters({ stripeKind: nextValue, offset: 0 });
  };

  const handleBooleanFilter =
    (field: 'activeOnly' | 'missingEndDate' | 'marketing' | 'codeOnly') =>
    (_: ChangeEvent<HTMLInputElement>, checked: boolean) => {
      if (field === 'codeOnly') {
        loadPortalUsersWithFilters({ codeOnly: checked, offset: 0 });
        return;
      }
      // If user asks for "active subscriptions" with no explicit platform selection,
      // default to paid platforms to match the paid-only "activeSubscriptions" widget.
      if (field === 'activeOnly' && checked === true) {
        const hasExplicitPlatform = portalFilters.platform.length > 0;
        const hasStripeKind = Boolean((portalFilters.stripeKind ?? '').trim());
        const hasCodeOnly = portalFilters.codeOnly === true;
        if (!hasExplicitPlatform && !hasStripeKind && !hasCodeOnly) {
          loadPortalUsersWithFilters({
            activeOnly: true,
            platform: ['stripe', 'apple', 'google'] as PortalSubscriptionPlatform[],
            offset: 0,
          });
          return;
        }
      }
      loadPortalUsersWithFilters({ [field]: checked } as Partial<PortalUserFilters>);
    };

  const handlePortalFixMissingEndDate = useCallback((user: User, subscription: Subscription) => {
    openForceExpiryDialogForSubscription(user, subscription);
  }, []);

  type FilterChip = { key: string; label: string; onDelete: () => void };

  const appliedFilterChips: FilterChip[] = (() => {
    if (!isPortalView) {
      return [];
    }
    const chips: FilterChip[] = [];
    if (portalFilters.codeOnly) {
      chips.push({
        key: 'codeOnly',
        label: t('portal_users.filters.code_only', { defaultValue: 'Kooditilaus' }),
        onDelete: () => loadPortalUsersWithFilters({ codeOnly: false }),
      });
    }
    const appliedStripeKind = (portalFilters.stripeKind ?? '').trim();
    if (!portalFilters.codeOnly && !appliedStripeKind) {
      portalFilters.platform.forEach((platform) => {
        chips.push({
          key: `platform-${platform}`,
          label: t(`portal_users.filters.${platform}`),
          onDelete: () => {
            const remaining = portalFilters.platform
              .filter((value) => value !== platform)
              .map((value) => value) as PortalSubscriptionPlatform[];
            loadPortalUsersWithFilters({ platform: remaining });
          },
        });
      });
    }
    if (!portalFilters.codeOnly && appliedStripeKind) {
      chips.push({
        key: 'stripeKind',
        label: `${t('portal_users.filters.stripe_kind_label', { defaultValue: 'Stripe type' })}: ${t(
          `portal_users.filters.stripe_kind.${appliedStripeKind}`,
          { defaultValue: appliedStripeKind }
        )}`,
        onDelete: () => loadPortalUsersWithFilters({ stripeKind: '', offset: 0 }),
      });
    }
    if (portalFilters.activeOnly) {
      chips.push({
        key: 'activeOnly',
        label: t('portal_users.filters.active_only'),
        onDelete: () => loadPortalUsersWithFilters({ activeOnly: false }),
      });
    }
    if (portalFilters.missingEndDate) {
      chips.push({
        key: 'missingEndDate',
        label: t('portal_users.filters.missing_end_date'),
        onDelete: () => loadPortalUsersWithFilters({ missingEndDate: false }),
      });
    }
    if (portalFilters.marketing) {
      chips.push({
        key: 'marketing',
        label: t('portal_users.filters.marketing'),
        onDelete: () => loadPortalUsersWithFilters({ marketing: false }),
      });
    }
    const appliedSearch = (portalFilters.search ?? '').trim();
    if (appliedSearch) {
      chips.push({
        key: 'search',
        label: `${t('search')}: ${appliedSearch}`,
        onDelete: () => {
          setPortalSearchInput('');
          loadPortalUsersWithFilters({ search: '', offset: 0 });
        },
      });
    }
    return chips;
  })();

  const statsItems = [
    { key: 'totalUsers', label: t('portal_users.stats.total_users'), value: portalStats.totalUsers },
    {
      key: 'activeSubscriptions',
      label: t('portal_users.stats.active_subscriptions'),
      value: portalStats.activeSubscriptions,
    },
    {
      key: 'appleSubscriptions',
      label: t('portal_users.stats.apple_subscriptions'),
      value: portalStats.appleSubscriptions,
    },
    {
      key: 'googleSubscriptions',
      label: t('portal_users.stats.google_subscriptions'),
      value: portalStats.googleSubscriptions,
    },
    {
      key: 'stripeSubscriptions',
      label: t('portal_users.stats.stripe_subscriptions'),
      value: portalStats.stripeSubscriptions,
    },
    {
      key: 'codeSubscriptions',
      label: t('portal_users.stats.code_subscriptions', { defaultValue: 'Kooditilaukset' }),
      value: portalStats.codeSubscriptions,
    },
    {
      key: 'adminSubscriptions',
      label: t('portal_users.stats.admin_subscriptions', { defaultValue: 'Admins' }),
      value: portalStats.adminSubscriptions,
    },
  ];

  const hasAppliedFilters = appliedFilterChips.length > 0;

  const handleClearFilters = () => {
    setPortalSearchInput('');
    resetPortalFilters();
  };

  const clearPortalSearch = () => {
    setPortalSearchInput('');
    loadPortalUsersWithFilters({ search: '', offset: 0 });
  };

  const navigateTo = () => {
    const baseUrl = getUserUrlByLocation(pathname);
    navigate(`${baseUrl}/create`);
  };

  // Table data (portal view is always server-driven page data).
  const tableData = useMemo(() => {
    if (!isPortalView) {
      return filteredData;
    }
    return users;
  }, [filteredData, isPortalView, users]);

  return (
    <Layout title={t(getUserTitleByLocation(pathname))}>
      <Grid container spacing={2} alignItems="center">
        <Grid item xs={12} md={6}>
          <AddButton onClick={navigateTo} text={t('add_new_user')} />
        </Grid>
        <Grid item xs={12} md={6}>
          {isPortalView ? (
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <FormControl size="small" sx={{ minWidth: 170 }}>
                <InputLabel id="portal-page-size-top-label">{t('portal_users.page_size_label')}</InputLabel>
                <Select
                  labelId="portal-page-size-top-label"
                  value={String(portalLimit)}
                  label={t('portal_users.page_size_label')}
                  onChange={handlePortalPageSizeChange}
                  disabled={portalUsersLoading}
                >
                  {PORTAL_PAGE_SIZE_OPTIONS.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Box>
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <TextField
                placeholder={t('search')}
                value={filter}
                onChange={(event: ChangeEvent<HTMLInputElement>) => {
                  setFilter(event.target.value);
                }}
                size="small"
                fullWidth
              />
              {filter && (
                <IconButton onClick={() => setFilter('')}>
                  <ClearIcon />
                </IconButton>
              )}
            </Box>
          )}
        </Grid>
      </Grid>
      {isPortalView && (
        <>
          <Paper sx={{ p: 2, mt: 3 }}>
            <Grid container spacing={2} alignItems="flex-start">
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <TextField
                    fullWidth
                    placeholder={t('search')}
                    value={portalSearchInput}
                    onChange={(event: ChangeEvent<HTMLInputElement>) => setPortalSearchInput(event.target.value)}
                    size="small"
                  />
                  {portalSearchInput && (
                    <IconButton onClick={clearPortalSearch}>
                      <ClearIcon />
                    </IconButton>
                  )}
                </Box>
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth size="small">
                  <InputLabel id="platform-filter-label">{t('portal_users.filters.platform_label')}</InputLabel>
                  <Select
                    labelId="platform-filter-label"
                    multiple
                    value={portalFilters.platform.slice() as string[]}
                    onChange={handlePlatformChange}
                    disabled={
                      portalUsersLoading || portalFilters.codeOnly || Boolean((portalFilters.stripeKind ?? '').trim())
                    }
                    input={<OutlinedInput label={t('portal_users.filters.platform_label')} />}
                    renderValue={(selected) =>
                      selected.length
                        ? (selected as PortalSubscriptionPlatform[])
                            .map((value) => t(`portal_users.filters.${value}`))
                            .join(', ')
                        : t('common.show_all')
                    }
                  >
                    {PORTAL_SUBSCRIPTION_PLATFORMS.map((platform) => {
                      const checked = portalFilters.platform.includes(platform);
                      return (
                        <MenuItem
                          key={platform}
                          value={platform}
                          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}
                        >
                          <span>{t(`portal_users.filters.${platform}`)}</span>
                          <Checkbox checked={checked} />
                        </MenuItem>
                      );
                    })}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                  {!portalFilters.codeOnly &&
                  ((portalFilters.stripeKind ?? '').trim() ||
                    portalFilters.platform.length === 0 ||
                    portalFilters.platform.includes('stripe')) ? (
                    <FormControl fullWidth size="small">
                      <InputLabel id="stripe-kind-filter-label">
                        {t('portal_users.filters.stripe_kind_label', { defaultValue: 'Stripe type' })}
                      </InputLabel>
                      <Select
                        labelId="stripe-kind-filter-label"
                        value={portalFilters.stripeKind ?? ''}
                        label={t('portal_users.filters.stripe_kind_label', { defaultValue: 'Stripe type' })}
                        onChange={handleStripeKindChange}
                        disabled={
                          portalUsersLoading ||
                          (portalFilters.platform.length > 0 && !portalFilters.platform.includes('stripe'))
                        }
                      >
                        <MenuItem value="">
                          {t('portal_users.filters.stripe_kind.all', { defaultValue: 'All' })}
                        </MenuItem>
                        {PORTAL_STRIPE_KINDS.map((kind) => (
                          <MenuItem key={kind} value={kind}>
                            {t(`portal_users.filters.stripe_kind.${kind}`, { defaultValue: kind })}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  ) : null}
                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'center',
                      gap: 2,
                      flexWrap: 'wrap',
                    }}
                  >
                    {isPortalView && (
                      <Button
                        variant="outlined"
                        onClick={handleDownloadPortalCsv}
                        disabled={exportingCsv}
                        startIcon={exportingCsv ? <CircularProgress size={16} /> : undefined}
                      >
                        {t('portal_users.download_csv')}
                      </Button>
                    )}
                    {hasAppliedFilters && (
                      <Typography variant="body2" color="text.secondary">
                        {t('portal_users.filtered_count', {
                          count: portalTotal,
                        })}
                      </Typography>
                    )}
                    <Button onClick={handleClearFilters} disabled={!hasAppliedFilters}>
                      {t('common.reset')}
                    </Button>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <FormGroup row>
                  <FormControlLabel
                    control={
                      <Checkbox checked={portalFilters.activeOnly} onChange={handleBooleanFilter('activeOnly')} />
                    }
                    label={t('portal_users.filters.active_only')}
                  />
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={portalFilters.missingEndDate}
                        onChange={handleBooleanFilter('missingEndDate')}
                      />
                    }
                    label={t('portal_users.filters.missing_end_date')}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={portalFilters.marketing} onChange={handleBooleanFilter('marketing')} />}
                    label={t('portal_users.filters.marketing')}
                  />
                  <FormControlLabel
                    control={<Checkbox checked={portalFilters.codeOnly} onChange={handleBooleanFilter('codeOnly')} />}
                    label={t('portal_users.filters.code_only', { defaultValue: 'Kooditilaus' })}
                  />
                </FormGroup>
              </Grid>
            </Grid>
          </Paper>
          {hasAppliedFilters && (
            <Stack direction="row" spacing={1} sx={{ mt: 2, flexWrap: 'wrap', gap: 1 }}>
              {appliedFilterChips.map((chip) => (
                <Chip key={chip.key} label={chip.label} onDelete={chip.onDelete} />
              ))}
            </Stack>
          )}
          <Grid container spacing={2} sx={{ mt: 2 }}>
            {statsItems.map((item) => (
              <Grid item xs={12} sm={6} md={4} lg={2} key={item.key}>
                <Paper sx={{ p: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    {item.label}
                  </Typography>
                  <Box sx={{ mt: 1, minHeight: 32, display: 'flex', alignItems: 'center' }}>
                    {portalStatsLoading ? (
                      <CircularProgress size={18} />
                    ) : (
                      <Typography variant="h5">{item.value}</Typography>
                    )}
                  </Box>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </>
      )}
      {isPortalView && portalUsersLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-start', mt: 2 }}>
          <CircularProgress size={20} />
        </Box>
      )}
      <Table
        tableType={USER_TABLE}
        data={tableData}
        selectedRow={selectedRow}
        setSelectedRow={(value) => setSelectedRow(value ? { id: value.id, value: value.value } : null)}
        isUserTable
        userRole={userRole}
        headerTitles={isPortalView ? PORTAL_TABLE_HEADERS : undefined}
        columnTemplate={isPortalView ? PORTAL_TABLE_TEMPLATE : undefined}
        onPortalFixMissingEndDate={isPortalView ? handlePortalFixMissingEndDate : undefined}
        hidePagination={isPortalView}
      />
      {isPortalView && (
        <Box
          sx={{
            mt: 2,
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            flexWrap: 'wrap',
            gap: 2,
          }}
        >
          <Typography variant="body2" color="text.secondary">
            {t('portal_users.pagination_summary', { from: portalShowingFrom, to: portalShowingTo, total: portalTotal })}
          </Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap', justifyContent: 'flex-end' }}>
            <Button variant="outlined" size="small" disabled={!canGoPrev} onClick={handlePortalPrev}>
              {t('common.previous', { defaultValue: 'Previous' })}
            </Button>
            <Pagination
              color="primary"
              count={portalTotalPages}
              page={portalCurrentPage}
              onChange={handlePortalPageChange}
              showFirstButton
              showLastButton
              disabled={disablePortalPagination || portalUsersLoading}
            />
            <Button variant="outlined" size="small" disabled={!canGoNext} onClick={handlePortalNext}>
              {t('common.next', { defaultValue: 'Next' })}
            </Button>
          </Box>
        </Box>
      )}
      <Dialog open={forceExpiryDialog.open} onClose={closeForceExpiryDialog} fullWidth maxWidth="xs">
        <DialogTitle>{t('portal_users.fix_missing_end_date_title')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t('portal_users.fix_missing_end_date_description')}
          </Typography>
          <TextField
            label={t('portal_users.fix_missing_end_date_period_label')}
            type="date"
            InputLabelProps={{ shrink: true }}
            value={forceExpiryDate}
            onChange={(event: ChangeEvent<HTMLInputElement>) => {
              setForceExpiryDate(event.target.value);
              setForceExpiryError(null);
            }}
            error={Boolean(forceExpiryError)}
            helperText={forceExpiryError || t('portal_users.fix_missing_end_date_period_helper')}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={forceExpiryMarkInactive}
                onChange={(event: ChangeEvent<HTMLInputElement>) => setForceExpiryMarkInactive(event.target.checked)}
              />
            }
            label={t('portal_users.fix_missing_end_date_mark_inactive')}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeForceExpiryDialog}>{t('cancel')}</Button>
          <Button onClick={submitForceExpiry} disabled={forceExpirySubmitting} variant="contained" color="primary">
            {forceExpirySubmitting && <CircularProgress size={16} sx={{ mr: 1 }} />}
            {t('portal_users.fix_missing_end_date_confirm')}
          </Button>
        </DialogActions>
      </Dialog>
      <Box sx={{ height: 24 }} />
    </Layout>
  );
});

export default UsersScreen;
